<script setup lang="ts">
  import { blurhashToCssGradientString } from '@unpic/placeholder'
  import { decode } from 'blurhash'
  import { computed, nextTick, onMounted, ref, watch } from 'vue'

  import { isValidBlurhash } from '../utils/blurhash.utils'

  const props = defineProps({
    hash: {
      type: String,
      required: true,
    },
    width: {
      type: Number,
      default: 32,
    },
    height: {
      type: Number,
      default: 32,
    },
    punch: {
      type: Number,
      default: 1,
    },
  })

  const canvasRef = ref<HTMLCanvasElement | null>(null)
  const isMounted = ref(false)

  const isHashValid = computed(() => isValidBlurhash(props.hash))
  const gradientCss = computed(() => (isHashValid.value ? blurhashToCssGradientString(props.hash) : ''))

  onMounted(async () => {
    isMounted.value = true
    await nextTick()
    drawBlurhash()
  })

  watch(
    () => props.hash,
    () => {
      if (isMounted.value) drawBlurhash()
    }
  )

  function drawBlurhash() {
    const canvas = canvasRef.value
    if (!canvas || !isHashValid.value) return

    const w = props.width ?? 32
    const h = props.height ?? 32

    let pixels: Uint8ClampedArray
    try {
      pixels = decode(props.hash, w, h, props.punch ?? 1)
    } catch {
      return
    }

    canvas.width = w
    canvas.height = h

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const imageData = ctx.createImageData(w, h)
    imageData.data.set(pixels)
    ctx.putImageData(imageData, 0, 0)
  }
</script>

<template>
  <div
    :style="{
      backgroundImage: gradientCss,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }"
  >
    <canvas v-if="isMounted" id="blurhash-canvas" ref="canvasRef" class="absolute inset-0 h-full w-full" />
  </div>
</template>
